import { useState, useEffect } from "react";
import { useQuestionnaireAnswersState } from "../contact-form/utils/questionnaireStorage";
import useLandingPageIds from "../../hooks/useLandingPageIds";
import { getLandingPageConfig } from "../../data/landingPageConfigs";
import { isMultiStudyConfig } from "../../types/landingPage";
import { MultiStudyMatcher } from "../../features/multi-study/matcher/MultiStudyMatcher";
import { convertStudyConfigToStudy } from "../../features/multi-study/utils/converters";
import type { Study } from "../../features/multi-study/types";
import {
  GitBranch,
  MapPin,
  Target,
  CheckCircle2,
  Circle,
  AlertCircle,
  XCircle
} from "lucide-react";

export function MultiStudyDebugTab() {
  const [questionAnswers] = useQuestionnaireAnswersState();
  const { variantId, masterId } = useLandingPageIds();

  // Force re-render when localStorage changes (poll every 500ms in dev mode)
  const [, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Get landing page config
  const landingPageId = variantId || masterId || "";
  const config = getLandingPageConfig(landingPageId);

  // Check for conditional routing (even if not multi-study)
  const hasConditionalRouting = config?.trialIdToAutoApply &&
    typeof config.trialIdToAutoApply === 'object' &&
    'conditionalRouting' in config.trialIdToAutoApply;

  // Check if this is a multi-study page OR has conditional routing
  if (!config || (!isMultiStudyConfig(config) && !hasConditionalRouting)) {
    return (
      <div className="p-4">
        <div className="text-center py-6 bg-gray-50 border rounded-md text-sm text-gray-500">
          <GitBranch size={24} className="mx-auto mb-2 text-gray-400" />
          <p>Not a multi-study or conditional routing page</p>
          <p className="text-xs mt-1">
            This tab shows data for multi-study pages (e.g. /vaccine-studies) or conditional routing pages (e.g. /covid-vaccine-50-64)
          </p>
        </div>
      </div>
    );
  }

  const { matchedStudyId, matchedStudyTrialId, questionAnswers: answers, multiStudyCascade } = questionAnswers;
  const zipcode = answers?.zipcode as string | undefined;

  // Read contact form values from localStorage (set by ContactForm component)
  const contactFormZipcode = import.meta.env.DEV
    ? localStorage.getItem('debug_contactFormZipcode') || undefined
    : undefined;
  const contactFormDOB = import.meta.env.DEV
    ? localStorage.getItem('debug_contactFormDOB') || undefined
    : undefined;
  const trialSiteId = import.meta.env.DEV
    ? localStorage.getItem('debug_trialSiteId') || undefined
    : undefined;

  // Use contact form zipcode if available, otherwise prescreening zipcode
  const currentZipcode = contactFormZipcode || zipcode;

  // Multi-study specific data (only if this is a multi-study page)
  const isMultiStudy = isMultiStudyConfig(config);
  const matchedStudy = isMultiStudy ? config.studies.find((s: any) => s.studyId === matchedStudyId) : undefined;

  // Get cascade tracking info
  const currentStudyId = multiStudyCascade?.currentStudyId;
  const failedStudyIds = multiStudyCascade?.failedStudyIds || [];
  const currentStudyIndex = multiStudyCascade?.currentStudyIndex ?? -1;

  // Calculate location-eligible studies when zipcode is available (only for multi-study)
  const allMatcherStudies: Study[] = isMultiStudy ? config.studies.map(convertStudyConfigToStudy) : [];
  const locationEligibleStudies: Study[] = isMultiStudy && zipcode
    ? new MultiStudyMatcher(allMatcherStudies).filterByLocation(zipcode)
    : [];

  // Calculate location-ineligible studies
  const locationIneligibleStudies: Study[] = isMultiStudy && zipcode
    ? allMatcherStudies.filter(
        study => !locationEligibleStudies.some(eligible => eligible.id === study.id)
      )
    : [];

  // Read conditional routing debug data from localStorage
  const conditionalRoutingData = import.meta.env.DEV
    ? (() => {
        try {
          const data = localStorage.getItem('debug_conditionalRouting');
          return data ? JSON.parse(data) : null;
        } catch {
          return null;
        }
      })()
    : null;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center text-purple-600">
        <GitBranch size={16} className="mr-2" />
        <h4 className="text-sm font-medium">
          {hasConditionalRouting ? "Conditional Trial Routing" : "Multi-Study Matching"}
        </h4>
      </div>

      {/* Landing Page Info */}
      <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
        <div className="text-xs font-medium text-purple-700 mb-2">
          Landing Page
        </div>
        <div className="text-sm font-mono text-purple-900">{landingPageId}</div>
        <div className="text-xs text-purple-600 mt-1">
          {hasConditionalRouting
            ? "Conditional trial routing enabled"
            : isMultiStudy
            ? `${config.studies?.length || 0} studies configured`
            : "Standard landing page"}
        </div>
      </div>

      {/* Conditional Routing Section */}
      {hasConditionalRouting && conditionalRoutingData && (
        <>
          {/* Patient Criteria */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-blue-50 px-3 py-2 border-b flex items-center">
              <Target size={14} className="text-blue-600 mr-2" />
              <span className="text-xs font-medium text-blue-700">
                Patient Criteria
              </span>
            </div>
            <div className="p-3 space-y-2">
              <div className="text-xs">
                <span className="text-gray-600">BMI:</span>{" "}
                <span className="font-mono font-medium">
                  {conditionalRoutingData.answers?.bmi ?? 'Not calculated'}
                </span>
              </div>
              <div className="text-xs">
                <span className="text-gray-600">Hypertension:</span>{" "}
                <span className="font-mono font-medium">
                  {conditionalRoutingData.answers?.hypertension ?? 'Not answered'}
                </span>
              </div>
            </div>
          </div>

          {/* Trial Waterfall */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-green-50 px-3 py-2 border-b flex items-center">
              <GitBranch size={14} className="text-green-600 mr-2" />
              <span className="text-xs font-medium text-green-700">
                Trial Waterfall (Priority Order)
              </span>
            </div>
            <div className="p-3 space-y-2">
              {conditionalRoutingData.eligibleTrials?.map((trialId: string, index: number) => {
                const isSelected = trialId === conditionalRoutingData.selectedTrialId;
                const isAttempted = index < (conditionalRoutingData.eligibleTrials?.length || 0);
                const wasSkippedDeprioritized = conditionalRoutingData.skippedDeprioritizedSite?.trialId === trialId;

                return (
                  <div
                    key={trialId}
                    className={`flex items-start text-xs p-2 rounded ${
                      isSelected
                        ? conditionalRoutingData.isDeprioritizedFallback
                          ? "bg-yellow-50 border-2 border-yellow-200"
                          : "bg-green-50 border-2 border-green-200"
                        : wasSkippedDeprioritized
                          ? "bg-orange-50 border border-orange-200"
                          : "bg-gray-50 border border-gray-200"
                    }`}
                  >
                    {isSelected ? (
                      <CheckCircle2 size={14} className={conditionalRoutingData.isDeprioritizedFallback ? "text-yellow-600" : "text-green-600"} />
                    ) : wasSkippedDeprioritized ? (
                      <AlertCircle size={14} className="text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                    ) : isAttempted ? (
                      <XCircle size={14} className="text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Circle size={14} className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium font-mono">{trialId}</div>
                      <div className={isSelected ? (conditionalRoutingData.isDeprioritizedFallback ? "text-yellow-700" : "text-green-700") : wasSkippedDeprioritized ? "text-orange-700" : "text-gray-600"}>
                        Priority {index + 1}
                        {isSelected && conditionalRoutingData.isDeprioritizedFallback && " • Deprioritized fallback ⚠️"}
                        {isSelected && !conditionalRoutingData.isDeprioritizedFallback && " • Site found ✓"}
                        {wasSkippedDeprioritized && !isSelected && ` • Site ${conditionalRoutingData.skippedDeprioritizedSite.siteId} deprioritized`}
                        {!isSelected && !wasSkippedDeprioritized && isAttempted && " • No site available"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Trial Info */}
          {conditionalRoutingData.selectedTrialId && (
            <div className={`border rounded-lg overflow-hidden ${conditionalRoutingData.isDeprioritizedFallback ? "bg-yellow-50 border-yellow-200" : "bg-green-50 border-green-200"}`}>
              <div className={`px-3 py-2 border-b flex items-center ${conditionalRoutingData.isDeprioritizedFallback ? "bg-yellow-100 border-yellow-200" : "bg-green-100 border-green-200"}`}>
                {conditionalRoutingData.isDeprioritizedFallback ? (
                  <AlertCircle size={14} className="text-yellow-700 mr-2" />
                ) : (
                  <CheckCircle2 size={14} className="text-green-700 mr-2" />
                )}
                <span className={`text-xs font-medium ${conditionalRoutingData.isDeprioritizedFallback ? "text-yellow-800" : "text-green-800"}`}>
                  Final Selected Trial {conditionalRoutingData.isDeprioritizedFallback && "(Deprioritized Fallback)"}
                </span>
              </div>
              <div className="p-3 space-y-2">
                <div className={`bg-white border rounded p-2 ${conditionalRoutingData.isDeprioritizedFallback ? "border-yellow-200" : "border-green-200"}`}>
                  <div className="text-xs font-medium text-gray-700">Trial ID</div>
                  <div className="text-sm font-mono text-gray-900 mt-0.5">
                    {conditionalRoutingData.selectedTrialId}
                  </div>
                </div>
                {(trialSiteId || conditionalRoutingData.selectedSiteId) && (
                  <div className={`bg-white border rounded p-2 ${conditionalRoutingData.isDeprioritizedFallback ? "border-yellow-200" : "border-green-200"}`}>
                    <div className="text-xs font-medium text-gray-700">Trial Site ID</div>
                    <div className="text-sm font-mono text-gray-900 mt-0.5">{trialSiteId || conditionalRoutingData.selectedSiteId}</div>
                  </div>
                )}
                {conditionalRoutingData.isDeprioritizedFallback ? (
                  <div className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded border border-yellow-300">
                    ⚠️ This site is in the deprioritized list (not yet activated). It was used as a fallback because no other trials had available sites for this location.
                    {conditionalRoutingData.skippedDeprioritizedSite && (
                      <div className="mt-1">
                        Skipped site: <span className="font-mono">{conditionalRoutingData.skippedDeprioritizedSite.siteId}</span> on trial <span className="font-mono">{conditionalRoutingData.skippedDeprioritizedSite.trialId}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-green-700 bg-green-100 p-2 rounded border border-green-300">
                    ℹ️ This trial was selected because it was the first in priority order with an available site for the entered zipcode.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status Messages */}
          {conditionalRoutingData.loading && (
            <div className="border rounded-lg overflow-hidden bg-blue-50 border-blue-200">
              <div className="p-3 flex items-center">
                <Circle size={16} className="text-blue-600 mr-2 animate-pulse" />
                <span className="text-sm text-blue-900">Checking trial site availability...</span>
              </div>
            </div>
          )}

          {conditionalRoutingData.isLastTrial && !conditionalRoutingData.selectedTrialId && (
            <div className="border rounded-lg overflow-hidden bg-orange-50 border-orange-200">
              <div className="p-3">
                <div className="flex items-start">
                  <AlertCircle size={16} className="text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-orange-900">All trials attempted</div>
                    <div className="text-xs text-orange-700 mt-1">
                      No trial sites available for the entered zipcode. The fallback trial should be used.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Location Filtering - Only for multi-study pages */}
      {isMultiStudy && zipcode && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-3 py-2 border-b flex items-center">
            <MapPin size={14} className="text-blue-600 mr-2" />
            <span className="text-xs font-medium text-blue-700">
              Location Filtering
            </span>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-xs">
              <span className="text-gray-600">Zipcode:</span>{" "}
              <span className="font-mono font-medium">{zipcode}</span>
            </div>
            <div className="text-xs">
              <span className="text-gray-600">Eligible Studies:</span>{" "}
              <span className="font-medium">
                {locationEligibleStudies.length} of {config.studies.length}
              </span>
            </div>
            {locationEligibleStudies.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  ✓ Eligible by Location:
                </div>
                <div className="space-y-1">
                  {locationEligibleStudies
                    .sort((a, b) => a.priority - b.priority)
                    .map((study) => {
                      const configStudy = config.studies.find(s => s.studyId === study.id);
                      return (
                        <div
                          key={study.id}
                          className="flex items-center text-xs p-2 rounded bg-blue-50 border border-blue-100"
                        >
                          <CheckCircle2 size={12} className="text-blue-600 mr-2 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium">{configStudy?.studyName || study.name}</div>
                            <div className="text-blue-600">Priority {study.priority}</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            {locationIneligibleStudies.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  ✗ Not Eligible by Location:
                </div>
                <div className="space-y-1">
                  {locationIneligibleStudies
                    .sort((a, b) => a.priority - b.priority)
                    .map((study) => {
                      const configStudy = config.studies.find(s => s.studyId === study.id);
                      return (
                        <div
                          key={study.id}
                          className="flex items-center text-xs p-2 rounded bg-gray-50 border border-gray-200"
                        >
                          <XCircle size={12} className="text-gray-400 mr-2 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-600">{configStudy?.studyName || study.name}</div>
                            <div className="text-gray-500">Priority {study.priority}</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Study Matching Status - Only for multi-study pages */}
      {isMultiStudy && (
        <div className="border rounded-lg overflow-hidden">
        <div className="bg-green-50 px-3 py-2 border-b flex items-center">
          <Target size={14} className="text-green-600 mr-2" />
          <span className="text-xs font-medium text-green-700">
            Study Matching Status
          </span>
        </div>
        <div className="p-3">
          {matchedStudyId ? (
            <div className="space-y-3">
              {/* Matched Study */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                <div className="flex items-start">
                  <CheckCircle2 size={16} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-green-900">
                      {matchedStudy?.studyName || matchedStudyId}
                    </div>
                    <div className="text-xs text-green-700 mt-1">
                      <span className="font-medium">Study ID:</span>{" "}
                      <span className="font-mono">{matchedStudyId}</span>
                    </div>
                    <div className="text-xs text-green-700 mt-1">
                      <span className="font-medium">Trial ID:</span>{" "}
                      <span className="font-mono">{matchedStudyTrialId}</span>
                    </div>
                    <div className="text-xs text-green-700 mt-1">
                      <span className="font-medium">Priority:</span>{" "}
                      {matchedStudy?.priority || "Unknown"}
                    </div>
                  </div>
                </div>
              </div>

              {/* All Configured Studies */}
              <div className="pt-2 border-t">
                <div className="text-xs font-medium text-gray-700 mb-2">
                  All Studies (by priority)
                </div>
                <div className="space-y-2">
                  {config.studies
                    .sort((a, b) => a.priority - b.priority)
                    .map((study) => {
                      const isMatched = study.studyId === matchedStudyId;
                      return (
                        <div
                          key={study.studyId}
                          className={`flex items-start text-xs p-2 rounded ${
                            isMatched
                              ? "bg-green-50 border border-green-200"
                              : "bg-gray-50"
                          }`}
                        >
                          {isMatched ? (
                            <CheckCircle2 size={14} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Circle size={14} className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <div className="font-medium">{study.studyName}</div>
                            <div className="text-gray-600 mt-0.5">
                              Priority {study.priority} • {study.eligibleZipCodes.length} eligible zipcodes
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          ) : currentStudyId ? (
            // Cascading in progress - show current study being attempted
            <div className="space-y-3">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                <div className="flex items-start">
                  <Circle size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0 animate-pulse" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-blue-900">
                      Currently Attempting
                    </div>
                    <div className="text-sm font-medium text-blue-900 mt-1">
                      {config.studies.find(s => s.studyId === currentStudyId)?.studyName || currentStudyId}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      <span className="font-medium">Study ID:</span>{" "}
                      <span className="font-mono">{currentStudyId}</span>
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      <span className="font-medium">Progress:</span>{" "}
                      Study {currentStudyIndex + 1} of {multiStudyCascade?.eligibleStudyIds.length || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Failed Studies */}
              {failedStudyIds.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    ✗ Failed Studies
                  </div>
                  <div className="space-y-1">
                    {failedStudyIds.map((studyId) => {
                      const study = config.studies.find(s => s.studyId === studyId);
                      return (
                        <div
                          key={studyId}
                          className="flex items-center text-xs p-2 rounded bg-red-50 border border-red-200"
                        >
                          <XCircle size={12} className="text-red-600 mr-2 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-red-900">{study?.studyName || studyId}</div>
                            <div className="text-red-700">Disqualified</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Remaining Studies */}
              {multiStudyCascade && currentStudyIndex < (multiStudyCascade.eligibleStudyIds.length - 1) && (
                <div className="pt-2">
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    ⏳ Remaining Studies
                  </div>
                  <div className="space-y-1">
                    {multiStudyCascade.eligibleStudyIds
                      .slice(currentStudyIndex + 1)
                      .map((studyId) => {
                        const study = config.studies.find(s => s.studyId === studyId);
                        return (
                          <div
                            key={studyId}
                            className="flex items-center text-xs p-2 rounded bg-gray-50 border border-gray-200"
                          >
                            <Circle size={12} className="text-gray-400 mr-2 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-700">{study?.studyName || studyId}</div>
                              <div className="text-gray-500">Pending</div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <AlertCircle size={20} className="mx-auto mb-2 text-gray-400" />
              <div className="text-sm text-gray-600">No study matched yet</div>
              <div className="text-xs text-gray-500 mt-1">
                {!zipcode
                  ? "Complete the prescreening to see matching results"
                  : "User may have been disqualified or no studies available"}
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Contact Form Trial ID - Only for multi-study */}
      {isMultiStudy && matchedStudyTrialId && (
        <div className="border rounded-lg overflow-hidden bg-yellow-50 border-yellow-200">
          <div className="bg-yellow-100 px-3 py-2 border-b border-yellow-200 flex items-center">
            <AlertCircle size={14} className="text-yellow-700 mr-2" />
            <span className="text-xs font-medium text-yellow-800">
              Contact Form Configuration
            </span>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-xs">
              <span className="text-gray-700 font-medium">Trial ID passed to ContactForm:</span>
            </div>
            <div className="bg-white border border-yellow-200 rounded p-2">
              <code className="text-sm font-mono text-yellow-900">
                {matchedStudyTrialId}
              </code>
            </div>
            <div className="text-xs text-yellow-700 mt-2">
              ℹ️ This is the trial ID that should be used for trial site lookup and referral creation.
            </div>
            {matchedStudy && (
              <div className="text-xs text-yellow-700 mt-1">
                📍 Expected trial sites for: <strong>{matchedStudy.studyName}</strong>
              </div>
            )}
            <div className="text-xs text-yellow-700 mt-2 bg-yellow-100 p-2 rounded border border-yellow-300">
              💡 <strong>Trial Site Lookup Flow:</strong>
              <ol className="list-decimal ml-4 mt-1 space-y-1">
                <li><strong>Enter Date of Birth</strong> → Trial ID gets resolved</li>
                <li><strong>Enter Zipcode</strong> → Zipcode gets validated</li>
                <li><strong>GraphQL Query</strong> → Fetches nearest trial site</li>
                <li><strong>Form Field Set</strong> → trialSiteId hidden field populated</li>
              </ol>
              <div className="mt-2 text-xs">
                Check browser console for detailed logs:
                <div className="font-mono text-xs mt-1 space-y-0.5">
                  <div>[ContactPage] Multi-Study Debug</div>
                  <div>[ContactForm] Trial ID Resolution</div>
                  <div>[ContactForm] Trial Site Lookup</div>
                  <div>[useNearestTrialSite] Query Result</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Submission Preview - Only for multi-study */}
      {isMultiStudy && matchedStudyTrialId && (
        <div className="border rounded-lg overflow-hidden bg-green-50 border-green-200">
          <div className="bg-green-100 px-3 py-2 border-b border-green-200 flex items-center">
            <CheckCircle2 size={14} className="text-green-700 mr-2" />
            <span className="text-xs font-medium text-green-800">
              Form Submission Preview
            </span>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-xs text-green-900 font-medium mb-2">
              When you submit the contact form, these values will be sent:
            </div>

            <div className="space-y-2">
              {/* Trial ID */}
              <div className="bg-white border border-green-200 rounded p-2">
                <div className="text-xs font-medium text-gray-700">Trial ID</div>
                <div className="text-sm font-mono text-gray-900 mt-0.5">{matchedStudyTrialId}</div>
              </div>

              {/* Trial Site ID */}
              <div className="bg-white border border-green-200 rounded p-2">
                <div className="text-xs font-medium text-gray-700">Trial Site ID</div>
                {trialSiteId ? (
                  <div className="text-sm font-mono text-gray-900 mt-0.5">{trialSiteId}</div>
                ) : (
                  <div className="text-sm text-orange-600 mt-0.5">
                    ⚠️ Not yet resolved - enter DOB and zipcode
                  </div>
                )}
              </div>

              {/* Zipcode */}
              <div className="bg-white border border-green-200 rounded p-2">
                <div className="text-xs font-medium text-gray-700">Zipcode</div>
                {currentZipcode ? (
                  <>
                    <div className="text-sm font-mono text-gray-900 mt-0.5">{currentZipcode}</div>
                    {contactFormZipcode && contactFormZipcode !== zipcode && (
                      <div className="text-xs text-blue-600 mt-1">
                        (Updated from {zipcode})
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-orange-600 mt-0.5">⚠️ Not entered</div>
                )}
              </div>

              {/* Date of Birth */}
              <div className="bg-white border border-green-200 rounded p-2">
                <div className="text-xs font-medium text-gray-700">Date of Birth</div>
                {contactFormDOB ? (
                  <div className="text-sm font-mono text-gray-900 mt-0.5">{contactFormDOB}</div>
                ) : (
                  <div className="text-sm text-orange-600 mt-0.5">⚠️ Not entered</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trial Site Lookup Requirements - Only for multi-study */}
      {isMultiStudy && matchedStudyTrialId && (
        <div className="border rounded-lg overflow-hidden bg-orange-50 border-orange-200">
          <div className="bg-orange-100 px-3 py-2 border-b border-orange-200 flex items-center">
            <Target size={14} className="text-orange-700 mr-2" />
            <span className="text-xs font-medium text-orange-800">
              Trial Site Lookup Requirements
            </span>
          </div>
          <div className="p-3 space-y-3">
            <div className="text-xs text-orange-900">
              For trial site lookup to execute, all of these must be true:
            </div>

            <div className="space-y-2">
              {/* Trial ID requirement */}
              <div className="flex items-start gap-2 p-2 bg-white rounded border border-orange-200">
                <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900">Trial ID</div>
                  <div className="text-xs text-gray-600 font-mono truncate">{matchedStudyTrialId}</div>
                  <div className="text-xs text-gray-500 mt-0.5">✓ Available (set after prescreening)</div>
                </div>
              </div>

              {/* Date of Birth requirement */}
              <div className="flex items-start gap-2 p-2 bg-white rounded border border-orange-200">
                <Circle size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-900">Date of Birth (validated)</div>
                  <div className="text-xs text-gray-600 mt-0.5">Triggers trial ID resolution in ContactForm</div>
                  <div className="text-xs text-orange-600 mt-0.5">⚠️ Must be entered in contact form (MM/DD/YYYY)</div>
                </div>
              </div>

              {/* Zipcode requirement */}
              <div className="flex items-start gap-2 p-2 bg-white rounded border border-orange-200">
                {currentZipcode ? (
                  <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <Circle size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-900">Zipcode (validated)</div>
                  {currentZipcode ? (
                    <>
                      <div className="text-xs text-gray-600 font-mono">{currentZipcode}</div>
                      {contactFormZipcode && contactFormZipcode !== zipcode ? (
                        <div className="text-xs text-blue-600 mt-0.5">
                          ✓ Updated in contact form (was: {zipcode || 'not set'})
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 mt-0.5">
                          ✓ {contactFormZipcode ? 'From contact form' : 'From prescreening'}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-orange-600 mt-0.5">⚠️ No zipcode entered</div>
                  )}
                </div>
              </div>

              {/* Country Code requirement */}
              <div className="flex items-start gap-2 p-2 bg-white rounded border border-orange-200">
                <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-900">Country Code</div>
                  <div className="text-xs text-gray-600 font-mono">{config.countryCode || 'US'}</div>
                  <div className="text-xs text-gray-500 mt-0.5">✓ Set from landing page config</div>
                </div>
              </div>

              {/* Locale Code requirement */}
              <div className="flex items-start gap-2 p-2 bg-white rounded border border-orange-200">
                <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-900">Locale Code</div>
                  <div className="text-xs text-gray-600 font-mono">EN</div>
                  <div className="text-xs text-gray-500 mt-0.5">✓ Always set to "EN"</div>
                </div>
              </div>
            </div>

            <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded border border-orange-300">
              <strong>⚠️ Common Issue:</strong> If trialSiteId is empty in API logs, check that:
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Date of birth was entered in contact form (not just prescreening)</li>
                <li>Zipcode passes validation (5 digits)</li>
                <li>GraphQL query returns trial sites (check Network tab)</li>
                <li>Prescreening status is "passed" (not "failed")</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Raw Data */}
      <details className="border rounded-lg overflow-hidden">
        <summary className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 cursor-pointer hover:bg-gray-100">
          Raw Matching Data
        </summary>
        <pre className="p-3 text-xs overflow-auto bg-white">
          {JSON.stringify(
            {
              matchedStudyId,
              matchedStudyTrialId,
              zipcode,
              prescreeningStatus: questionAnswers.prescreeningStatus,
              multiStudyCascade,
            },
            null,
            2
          )}
        </pre>
      </details>
    </div>
  );
}
